/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect } from "react";
import OrdersTable from "./OrdersTable";
import { SearchSection } from "../buscador/SearchSection";
import { MyOrdersTable } from "./MyOrdersTable";
import { nanoid } from "nanoid";
import { getPrinterService } from "../../services/printer/MockPrinterService";
import { toast } from "react-hot-toast";
import { orderService } from "../../services/OrderService";

const WaiterMode = ({ orders, getTotal, onOrdersUpdate }) => {
  const [orderItems, setOrderItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [isArqueoActive, setIsArqueoActive] = useState(false);
  const printerService = getPrinterService();

  // Efecto para inicializar y mantener actualizado el estado del arqueo
  useEffect(() => {
    const savedState = localStorage.getItem("arqueoActive");
    console.log("WaiterMode - Estado inicial del arqueo:", savedState);

    // Asegurarse de convertir el string a booleano correctamente
    setIsArqueoActive(savedState === "true");

    const handleArqueoChange = (event) => {
      console.log(
        "WaiterMode - Cambio detectado en arqueo:",
        event.detail.isActive
      );
      setIsArqueoActive(event.detail.isActive);
    };

    window.addEventListener("arqueoStateChanged", handleArqueoChange);
    return () =>
      window.removeEventListener("arqueoStateChanged", handleArqueoChange);
  }, []);

  const filteredOrders = selectedTable
    ? orders.filter(
        (order) =>
          order.mesaId === selectedTable.id ||
          order.table === selectedTable.description
      )
    : orders;

  const handleOrderSuccess = useCallback(async () => {
    setOrderItems([]);
    await onOrdersUpdate();
  }, [onOrdersUpdate]);

  const handleTableSelect = useCallback((tableInfo) => {
    setSelectedTable(tableInfo);
    setOrderItems([]);
  }, []);

  const handleQuantityChange = useCallback(
    (product, quantity, selectedAddon) => {
      setOrderItems((prevItems) => {
        const existingIndex = prevItems.findIndex(
          (item) =>
            item.product._id === product._id &&
            item.selectedAddon === selectedAddon
        );

        if (quantity === 0) {
          if (existingIndex >= 0) {
            return prevItems.filter((_, index) => index !== existingIndex);
          }
          return prevItems;
        }

        if (existingIndex >= 0) {
          return prevItems.map((item, index) =>
            index === existingIndex ? { ...item, quantity } : item
          );
        }

        if (quantity > 0) {
          const newItem = {
            id: nanoid(),
            product: {
              ...product,
              _id: product._id,
            },
            quantity,
            selectedAddon,
            comment: "",
          };
          return [...prevItems, newItem];
        }

        return prevItems;
      });
    },
    []
  );

  const getCurrentQuantity = useCallback(
    (productId) => {
      return orderItems
        .filter((item) => item.product._id === productId)
        .reduce((total, item) => total + item.quantity, 0);
    },
    [orderItems]
  );

  const calculateOrderTotal = useCallback((items) => {
    return items.reduce(
      (total, item) => total + item.product.price * item.quantity,
      0
    );
  }, []);

  const handleSendOrder = useCallback(async () => {
    try {
      if (!selectedTable) {
        toast.error("Por favor, seleccione una mesa");
        return;
      }

      if (orderItems.length === 0) {
        toast.error("No hay items en el pedido");
        return;
      }

      // Usar el estado local directamente en lugar de leer de localStorage
      console.log("WaiterMode - Estado del arqueo al enviar:", isArqueoActive);
      console.log(
        "WaiterMode - Tipo de isArqueoActive:",
        typeof isArqueoActive
      );

      const orderData = orderService.formatOrderData(
        orderItems,
        selectedTable,
        null, // userId
        isArqueoActive // Pasar el estado del componente directamente
      );

      console.log("WaiterMode - orderData antes de enviar:", orderData);
      console.log(
        "WaiterMode - isArqueoActive en orderData:",
        orderData.isArqueoActive
      );
      console.log(
        "WaiterMode - tipo de isArqueoActive:",
        typeof orderData.isArqueoActive
      );

      const result = await orderService.createOrder(orderData);
      await handleOrderSuccess();
      toast.success("Pedido enviado correctamente");
    } catch (error) {
      console.error("Error al enviar el pedido:", error);
      toast.error(error.message || "Error al enviar el pedido");
    }
  }, [
    selectedTable,
    orderItems,
    handleOrderSuccess,
    calculateOrderTotal,
    isArqueoActive,
  ]);

  const handlePayment = useCallback(
    async (orderId, method) => {
      try {
        const order = orders.find((o) => o._id === orderId || o.id === orderId);
        if (!order) throw new Error("Orden no encontrada");

        await orderService.verifyPayment(orderId, method);

        const printOrder = {
          _id: order._id || order.id,
          id: order._id || order.id,
          mesaId: order.mesaId || order.table,
          descriptionTable: order.table || order.descriptionTable,
          date: order.date,
          payment: method,
          items: (order.products || order.items).map((item) => ({
            name: item.name,
            price: item.price || item.total / (item.quantity || 1),
            quantity: item.quantity || 1,
            selectedAddon: item.selectedAddon,
          })),
        };

        const handlers = Array.from(printerService.printerHandlers.entries());

        for (const [printerId, handler] of handlers) {
          if (handler.config.autoCustomerBills) {
            await printerService.printCustomerBill(printOrder, printerId);
          }
        }

        await onOrdersUpdate();
        toast.success("Pago procesado correctamente");
      } catch (error) {
        console.error("Error:", error);
        toast.error(`Error: ${error.message}`);
        throw error;
      }
    },
    [orders, onOrdersUpdate, printerService]
  );

  return (
    <div className="space-y-8">
      <OrdersTable
        orders={filteredOrders}
        getTotal={getTotal}
        handlePayment={handlePayment}
        selectedTable={selectedTable}
        onTableSelect={handleTableSelect}
        onOrdersUpdate={onOrdersUpdate}
      />

      {selectedTable && (
        <>
          <MyOrdersTable
            orderItems={orderItems}
            selectedTable={selectedTable}
            onRemoveItem={(itemId) => {
              setOrderItems((prevItems) =>
                prevItems.filter((item) => item.id !== itemId)
              );
            }}
            onUpdateComment={(itemId, comment) => {
              setOrderItems((prevItems) =>
                prevItems.map((item) =>
                  item.id === itemId ? { ...item, comment } : item
                )
              );
            }}
            onOrderSuccess={handleOrderSuccess}
            getTotal={calculateOrderTotal}
            onQuantityChange={handleQuantityChange}
            onSendOrder={handleSendOrder}
          />

          <SearchSection
            onQuantityChange={handleQuantityChange}
            getCurrentQuantity={getCurrentQuantity}
            selectedTable={selectedTable}
          />
        </>
      )}
    </div>
  );
};

export default WaiterMode;
